import { Button, Card } from "@peer-start/ui/reducate";
import { BadgeCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { StartingTeacherRouteFrame } from "./StartingTeacherRouteFrame.js";
import { StartingTeacherStatusPill } from "./StartingTeacherStatusPill.js";
import {
  competencyStatus,
  highlightedCompetencyId,
  isActiveSkill,
  skillListAction,
  skillCompletion,
  skillStatus,
  visibleTopics,
  type CompetencyRef,
  type StartingTeacherProgress,
} from "../data/starting-teacher-route.js";

export function StartingTeacherCompetency({
  competency,
  progress,
  aside,
  onOpenSkill,
}: {
  competency: CompetencyRef;
  progress: StartingTeacherProgress;
  aside: ReactNode;
  onOpenSkill: (skillId: string, topicId: string, activate: boolean) => void;
}) {
  const { t } = useTranslation();
  const counts = {
    done: competency.skills.filter((skill) => skillCompletion(skill, progress).complete).length,
    total: competency.skills.length,
  };
  const currentCompetency = highlightedCompetencyId(progress) === competency.id;

  return (
    <StartingTeacherRouteFrame
      aside={aside}
      asideAlign="content"
      controlId="starting-teacher.competency"
      header={
        <>
          <p className="flex flex-wrap items-center gap-2 text-xs">
            <StartingTeacherStatusPill status={competencyStatus(competency, progress, currentCompetency)} />
            <span className="text-muted-foreground">{t("startingTeacher.skillsDone", counts)}</span>
          </p>
          <h1 className="mt-4 text-3xl font-medium tracking-tight">
            {t(`startingTeacher.competencies.${competency.id}.title`)}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {t(`startingTeacher.competencies.${competency.id}.summary`)}
          </p>
        </>
      }
    >
      <ol className="space-y-3" data-control-id="starting-teacher.skills">
        {competency.skills.map((skill) => {
          const active = isActiveSkill(skill.id, competency, progress);
          const action = skillListAction(skill.id, competency, progress);
          const status = skillStatus(skill, progress, active);
          const topics = visibleTopics(skill, progress);
          const firstTopic = topics.find((entry) => entry.id === progress.activeTopicId) ?? topics[0];
          return (
            <li key={skill.id}>
              <Card>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    {status ? <StartingTeacherStatusPill status={status} /> : null}
                    {progress.skillVerifiedAt[skill.id] ? (
                      <p
                        className={`${status ? "mt-2" : ""} inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground`}
                        data-control-id={`starting-teacher.skill.verified.${skill.id}`}
                      >
                        <BadgeCheck aria-hidden="true" className="size-3.5" />
                        {t("startingTeacher.verifiedByMentor")}
                      </p>
                    ) : null}
                    <h2
                      className={`${status || progress.skillVerifiedAt[skill.id] ? "mt-3" : ""} text-lg font-medium`}
                    >
                      {t(`startingTeacher.skills.${skill.id}.title`)}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {t(`startingTeacher.skills.${skill.id}.summary`)}
                    </p>
                  </div>
                  {firstTopic ? (
                    <Button
                      controlId={`starting-teacher.skill.open.${skill.id}`}
                      onClick={() =>
                        onOpenSkill(
                          skill.id,
                          progress.activeTopicId && active ? progress.activeTopicId : firstTopic.id,
                          action !== "jump",
                        )
                      }
                      type="button"
                      variant={action === "continue" ? "primary" : "secondary"}
                    >
                      {t(
                        action === "continue"
                          ? "startingTeacher.continueRoute"
                          : action === "start"
                            ? "startingTeacher.start"
                            : "startingTeacher.jumpTo",
                      )}
                    </Button>
                  ) : null}
                </div>
              </Card>
            </li>
          );
        })}
      </ol>
    </StartingTeacherRouteFrame>
  );
}
